import { Download, File, Folder, Trash2 } from "lucide-react";
import type { JSX } from "react";

import type { FileNodeDTO } from "../../../common/api/types.js";
import { ShareStatusIcon, visibilityFromShares } from "../../../common/components/ShareStatusIcon.js";
import { useFileShares } from "../query/fileQueries.js";

/** Größe in einer Form, die man beim Überfliegen erfasst. */
export function formatSize(bytes: number | null): string {
  if (bytes === null) {
    return "";
  }
  const units = ["B", "kB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = unit === 0 ? value : Math.round(value * 10) / 10;
  return `${rounded.toLocaleString("de-DE")} ${units[unit]}`;
}

export function FileList({
  entries,
  onOpen,
  onDownload,
  onShare,
  onTrash,
  showOwner,
  selectedId,
}: {
  entries: FileNodeDTO[];
  onOpen: (node: FileNodeDTO) => void;
  onDownload: (node: FileNodeDTO) => void;
  onShare: (node: FileNodeDTO) => void;
  onTrash: (node: FileNodeDTO) => void;
  showOwner: boolean;
  selectedId?: string | null;
}): JSX.Element {
  if (entries.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-line p-8 text-center text-sm text-ink-muted">
        Hier ist noch nichts.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line">
      {entries.map((entry) => (
        <li id={`file-${entry.id}`} key={entry.id} className={[
          "flex items-center gap-3 px-3 py-2.5",
          selectedId === entry.id ? "bg-brand/10 ring-1 ring-inset ring-brand" : "",
        ].join(" ")}>
          <button
            type="button"
            onClick={() => onOpen(entry)}
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
          >
            {entry.kind === "FOLDER" ? (
              <Folder className="h-5 w-5 shrink-0 text-brand" />
            ) : (
              <File className="h-5 w-5 shrink-0 text-ink-muted" />
            )}
            <span className="min-w-0">
              <span className="block truncate text-sm text-ink">{entry.name}</span>
              <span className="block truncate text-xs text-ink-muted">
                {[
                  showOwner && !entry.ownedByMe ? entry.ownerDisplayName : null,
                  entry.kind === "FILE" ? formatSize(entry.sizeBytes) : null,
                  entry.level === "VIEW" ? "nur lesen" : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
          </button>

          {entry.kind === "FILE" ? (
            <button
              type="button"
              onClick={() => onDownload(entry)}
              title="Herunterladen"
              className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink"
            >
              <Download className="h-4 w-4" />
            </button>
          ) : null}

          <button
            type="button"
            onClick={() => onShare(entry)}
            title="Freigeben"
            className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink"
          >
            <FileShareStatus nodeId={entry.id} />
          </button>

          {entry.level === "MANAGE" ? (
            <button
              type="button"
              onClick={() => onTrash(entry)}
              title="Löschen"
              className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-danger"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function FileShareStatus({ nodeId }: { nodeId: string }): JSX.Element {
  const shares = useFileShares(nodeId);
  return <ShareStatusIcon visibility={visibilityFromShares(shares.data?.shares ?? [])} />;
}
